/**
 * The reading form (fieldiq-api#1248, WT-3) — the office's "Add reading" and
 * "Correct", and the phone's "Enter reading". One component, two skins.
 *
 * EVERY FIELD IS TYPED IN A UNIT THE HAND PICKS. The picker defaults to the
 * tenant's unit for that field (`defaultUnit` from /reading-fields) and offers
 * every unit the kind accepts (/meta `quantityUnits`). The value goes to the
 * API exactly as typed, with its unit; the API keeps it and computes the
 * metric copy. Nothing here converts a number.
 *
 * GROUPS are the package in the order a hand reads it: pressures, choke &
 * meter, rates, cumulatives, tank gauges (one per test tank on the job), sand
 * dump, H2S / CO2 / BS&W.
 *
 * A CORRECTION sends only what changed (a field cleared to blank is sent blank,
 * which the API reads as "clear it"); anything not named is carried from the
 * reading being corrected. It needs a reason, checked here before it is sent.
 */
import React, { useMemo, useState } from 'react';
import {
  wtFieldUnits,
  wtGroupFields,
  wtReadingBody,
  wtUnitLabel,
  type WtReadingEntry,
} from '../../lib/well-testing';
import { WT_INPUT, WT_PRIMARY, WT_SECONDARY, WtField, WtFormError } from './WtBits';

/** A reading field, camelCase (the phone screen maps its snake_case rows into this). */
export interface ReadingFormField {
  key: string;
  label: string;
  kind: string;
  group: string;
  defaultUnit: string;
}

export interface ReadingFormTank {
  equipmentId: string;
  label: string;
}

export interface ReadingFormInitial {
  entries: Record<string, WtReadingEntry>;
  tanks: Record<string, WtReadingEntry>;
}

const LEVEL_UNITS_FALLBACK = ['mm', 'cm', 'm', 'in', 'ft_in'];

export function ReadingForm({
  fields,
  quantityUnits,
  tanks,
  tankDefaultUnit,
  initial,
  mode,
  variant = 'office',
  pending,
  error,
  submitLabel,
  onCancel,
  onSubmit,
  testId = 'wt-reading-form',
}: {
  fields: ReadingFormField[];
  quantityUnits: Record<string, { metric: string; accepted: string[] }> | null | undefined;
  tanks: ReadingFormTank[];
  /** The tenant's tank-gauge unit (mm metric, ft_in imperial). */
  tankDefaultUnit: string;
  /** Prefill (the phone's last reading) or the reading being corrected. */
  initial?: ReadingFormInitial | null;
  mode: 'new' | 'correct';
  variant?: 'office' | 'phone';
  pending: boolean;
  error: string | null;
  submitLabel: string;
  onCancel?: () => void;
  onSubmit: (body: Record<string, unknown>) => void;
  testId?: string;
}) {
  const seed = useMemo(() => {
    const entries: Record<string, WtReadingEntry> = {};
    for (const f of fields) {
      const i = initial?.entries[f.key];
      entries[f.key] = { value: i?.value ?? '', unit: i?.unit || f.defaultUnit };
    }
    const tk: Record<string, WtReadingEntry> = {};
    for (const t of tanks) {
      const i = initial?.tanks[t.equipmentId];
      tk[t.equipmentId] = { value: i?.value ?? '', unit: i?.unit || tankDefaultUnit };
    }
    return { entries, tanks: tk };
  }, [fields, tanks, initial, tankDefaultUnit]);

  const [entries, setEntries] = useState<Record<string, WtReadingEntry>>(seed.entries);
  const [tankLevels, setTankLevels] = useState<Record<string, WtReadingEntry>>(seed.tanks);
  const [readAt, setReadAt] = useState('');
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const phone = variant === 'phone';
  const input = phone ? `${WT_INPUT} h-12 text-base` : WT_INPUT;
  const groups = wtGroupFields(fields);
  const levelUnits = quantityUnits?.level?.accepted?.length
    ? Array.from(new Set([...quantityUnits.level.accepted, tankDefaultUnit]))
    : LEVEL_UNITS_FALLBACK;

  const setEntry = (key: string, patch: Partial<WtReadingEntry>) =>
    setEntries((cur) => ({ ...cur, [key]: { ...cur[key], ...patch } }));
  const setTank = (id: string, patch: Partial<WtReadingEntry>) =>
    setTankLevels((cur) => ({ ...cur, [id]: { ...cur[id], ...patch } }));

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setLocalError(null);
    if (mode === 'correct') {
      if (!reason.trim()) return setLocalError('Say why this reading is being corrected.');
      const body: Record<string, unknown> = { correction_reason: reason.trim() };
      for (const f of fields) {
        const now = entries[f.key];
        const was = seed.entries[f.key];
        if (now.value.trim() === was.value.trim() && (f.kind === 'unitless' || now.unit === was.unit)) continue;
        body[f.key] = now.value.trim();
        if (f.kind !== 'unitless' && now.value.trim() !== '') body[`${f.key}_unit`] = now.unit;
      }
      const tanksChanged = tanks.some((t) => {
        const a = tankLevels[t.equipmentId];
        const b = seed.tanks[t.equipmentId];
        return a.value.trim() !== b.value.trim() || a.unit !== b.unit;
      });
      if (tanksChanged) {
        body.tanks = tanks
          .filter((t) => tankLevels[t.equipmentId].value.trim() !== '')
          .map((t) => ({ equipment_id: t.equipmentId, level: tankLevels[t.equipmentId].value.trim(), level_unit: tankLevels[t.equipmentId].unit }));
      }
      if (readAt) body.read_at = readAt;
      if (notes.trim()) body.notes = notes.trim();
      if (Object.keys(body).length === 1) return setLocalError('Change at least one value to correct this reading.');
      return onSubmit(body);
    }
    const body = wtReadingBody(
      fields,
      entries,
      tanks.map((t) => ({ equipmentId: t.equipmentId, level: tankLevels[t.equipmentId].value, unit: tankLevels[t.equipmentId].unit })),
      { readAt: readAt || undefined, notes },
    );
    const hasValue = fields.some((f) => body[f.key] !== undefined) || Array.isArray(body.tanks);
    if (!hasValue) return setLocalError('Enter at least one value or tank gauge before saving.');
    onSubmit(body);
  };

  const unitPicker = (value: string, units: string[], onChange: (u: string) => void, label: string) => (
    // The width lives on a wrapper: the input class carries w-full, which beats a w-28 on the same element.
    <div className="w-28 shrink-0">
      <select className={input} aria-label={`${label} unit`} value={value} onChange={(e) => onChange(e.target.value)}>
        {units.map((u) => (
          <option key={u} value={u}>
            {wtUnitLabel(u)}
          </option>
        ))}
      </select>
    </div>
  );

  const fieldRow = (f: ReadingFormField) => {
    const e = entries[f.key];
    const units = wtFieldUnits(f, quantityUnits);
    return (
      <WtField key={f.key} label={f.label}>
        <div className="flex gap-2" data-field={f.key}>
          <input
            className={`${input} min-w-0 flex-1`}
            aria-label={f.label}
            inputMode="decimal"
            value={e.value}
            onChange={(ev) => setEntry(f.key, { value: ev.target.value })}
          />
          {units.length ? (
            unitPicker(e.unit, units, (u) => setEntry(f.key, { unit: u }), f.label)
          ) : (
            <span className="flex w-28 shrink-0 items-center text-sm font-bold text-fieldiq-muted" aria-label={`${f.label} unit`}>
              {wtUnitLabel(f.defaultUnit)}
            </span>
          )}
        </div>
      </WtField>
    );
  };

  const grid = phone ? 'grid gap-3' : 'grid gap-4 sm:grid-cols-2 lg:grid-cols-3';
  const heading = phone
    ? 'pt-2 text-xs font-black uppercase tracking-[0.12em] text-fieldiq-muted'
    : 'text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted';

  const tankGroup = tanks.length > 0 && (
    <section key="tanks" data-group="tanks" className="space-y-3">
      <h3 className={heading}>Tank gauges</h3>
      <div className={grid}>
        {tanks.map((t) => (
          <WtField key={t.equipmentId} label={t.label}>
            <div className="flex gap-2" data-tank={t.equipmentId}>
              <input
                className={`${input} min-w-0 flex-1`}
                aria-label={`${t.label} level`}
                inputMode={tankLevels[t.equipmentId].unit === 'ft_in' ? 'text' : 'decimal'}
                placeholder={tankLevels[t.equipmentId].unit === 'ft_in' ? `10' 6"` : undefined}
                value={tankLevels[t.equipmentId].value}
                onChange={(ev) => setTank(t.equipmentId, { value: ev.target.value })}
              />
              {unitPicker(tankLevels[t.equipmentId].unit, levelUnits, (u) => setTank(t.equipmentId, { unit: u }), `${t.label} level`)}
            </div>
          </WtField>
        ))}
      </div>
    </section>
  );

  return (
    <form onSubmit={submit} className={phone ? 'space-y-4' : 'space-y-5 px-4 py-4'} data-testid={testId} noValidate>
      <WtFormError message={localError ?? error} />
      {mode === 'correct' && (
        <WtField label="Reason for the correction">
          <textarea
            className={`${input} h-20 py-2`}
            aria-label="Reason for the correction"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </WtField>
      )}
      {groups.map((g) => (
        <React.Fragment key={g.key}>
          {g.key === 'sand' && tankGroup}
          <section data-group={g.key} className="space-y-3">
            <h3 className={heading}>{g.label}</h3>
            <div className={grid}>{g.fields.map(fieldRow)}</div>
          </section>
        </React.Fragment>
      ))}
      {!groups.some((g) => g.key === 'sand') && tankGroup}
      <section className="space-y-3">
        <h3 className={heading}>Time & notes</h3>
        <div className={grid}>
          <WtField label={mode === 'correct' ? 'Read at (blank keeps the original time)' : 'Read at (blank = now)'}>
            <input type="datetime-local" className={input} aria-label="Read at" value={readAt} onChange={(e) => setReadAt(e.target.value)} />
          </WtField>
          <WtField label="Notes">
            <input className={input} aria-label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </WtField>
        </div>
      </section>
      <div className={phone ? 'grid gap-2' : 'flex flex-wrap justify-end gap-2'}>
        {onCancel && (
          <button type="button" className={WT_SECONDARY} onClick={onCancel} disabled={pending}>
            Cancel
          </button>
        )}
        <button type="submit" className={phone ? `${WT_PRIMARY} h-12 w-full justify-center text-base` : WT_PRIMARY} disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
